import { parse } from "./parser";
import type { ParseOptions } from "./types";

/** A single word within a {@link Cue}, timed in **seconds**. */
export interface CueWord {
  /** Start time, in seconds. */
  start: number;
  /** End time, in seconds. */
  end: number;
  /** The word text (no surrounding whitespace). */
  text: string;
}

/**
 * A neutral timed-text cue in **seconds** — the media-domain unit used by
 * players and editors (`video.currentTime`). Deliberately minimal and
 * source-agnostic: it's neither tied to subtitle-file specifics (no VTT
 * `settings`, no milliseconds) nor to transcription internals.
 *
 * `words` is optional word-level timing: transcription sources (WhisperJson)
 * carry it natively; for SRT/VTT it can be synthesized — see
 * {@link synthesizeWords}.
 */
export interface Cue {
  /** Optional opaque editor identity, preserved by edit operations. */
  id?: string;
  /** Start time, in seconds. */
  start: number;
  /** End time, in seconds. */
  end: number;
  /** The cue text. */
  text: string;
  /** Optional word-level timing, in order. */
  words?: CueWord[];
}

/**
 * Synthesize word-level timing for a cue that lacks it, splitting `text` on
 * whitespace and distributing the cue's duration across words **proportionally
 * to their character length** (longer words take longer to say — the same
 * approximation the Remotion renderer uses). Returns the cue's existing `words`
 * untouched if present, and `[]` for empty/whitespace-only text.
 */
export function synthesizeWords(cue: Cue): CueWord[] {
  if (cue.words) return cue.words;
  const tokens = cue.text.split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return [];

  const totalChars = tokens.reduce((sum, token) => sum + token.length, 0);
  const duration = Math.max(0, cue.end - cue.start);
  const words: CueWord[] = [];
  let cursor = cue.start;
  for (const token of tokens) {
    const span = totalChars > 0 ? (duration * token.length) / totalChars : 0;
    const start = cursor;
    const end = cursor + span;
    words.push({ start, end, text: token });
    cursor = end;
  }
  // Guard against float drift so the last word ends exactly at the cue end.
  const last = words[words.length - 1];
  if (last) last.end = cue.end;
  return words;
}

/**
 * A {@link CueWord} annotated with whether a word break preceded it in the
 * source text. Word-level highlighting (karaoke) draws each word separately, so
 * it needs to know where the *original* spaces were rather than assuming one
 * between every token — otherwise scripts written without inter-word spaces
 * (Thai, Lao, Khmer, CJK), whose transcription tokens abut with no space, get a
 * spurious gap injected between every token. See {@link layoutCueWords}.
 */
export interface LaidOutWord extends CueWord {
  /**
   * True if whitespace separated this word from the previous one in the source
   * text (a real word break). Always false for the first word. False for
   * abutting tokens such as consecutive Thai syllables.
   */
  leadingSpace: boolean;
}

/**
 * Resolve a cue's words for layout, tagging each with whether a space preceded
 * it in `cue.text`. Uses {@link synthesizeWords} for the tokens, then walks them
 * through `cue.text` in order to recover the real separators: `words` are an
 * in-order partition of the text, so the whitespace between consecutive tokens
 * is exactly what should be reproduced on screen.
 *
 * Any run of whitespace counts as a single word break (rendered as one space);
 * abutting tokens get no space. If the words don't cleanly partition the text
 * (malformed input), falls back to treating every gap as a single space — the
 * historical behavior — so well-formed Latin cues are unaffected.
 */
export function layoutCueWords(cue: Cue): LaidOutWord[] {
  const words = synthesizeWords(cue);
  const text = cue.text;
  const out: LaidOutWord[] = [];
  let cursor = 0;
  for (let i = 0; i < words.length; i++) {
    const word = words[i] as CueWord;
    let j = cursor;
    while (j < text.length && /\s/.test(text[j] as string)) j++;
    if (!text.startsWith(word.text, j)) {
      // Partition mismatch — fall back to a space between every token.
      return words.map((w, idx) => ({ ...w, leadingSpace: idx > 0 }));
    }
    out.push({ ...word, leadingSpace: i > 0 && j > cursor });
    cursor = j + word.text.length;
  }
  return out;
}

/**
 * Parse an SRT or WebVTT string into {@link Cue}s (seconds), dropping empty
 * cues and sorting by start time. Builds on {@link parse}; format is
 * auto-detected unless given in `options`.
 */
export function parseToCues(input: string, options?: ParseOptions): Cue[] {
  return parse(input, options)
    .map((caption) => ({
      start: caption.start / 1000,
      end: caption.end / 1000,
      text: caption.text.trim(),
    }))
    .filter((cue) => cue.text.length > 0)
    .sort((a, b) => a.start - b.start);
}
